import { useState, useCallback, useEffect, useRef } from "react";
import type { ProfileQueryRequest } from "@/services/api/companies";
import type { PaginationState } from "@tanstack/react-table";

export interface UseStaffFiltersOptions {
  initialFilters: Partial<ProfileQueryRequest>;
  urlFilters: Partial<ProfileQueryRequest>;
  onFiltersChange: (
    filters: Partial<ProfileQueryRequest>,
    pagination: PaginationState
  ) => void;
  onResetPagination: () => void;
  currentPagination: PaginationState;
}

export interface UseStaffFiltersReturn {
  filters: Partial<ProfileQueryRequest>;
  setFilters: (filters: Partial<ProfileQueryRequest>) => void;
  handleFilterChange: (
    key: keyof ProfileQueryRequest,
    value: string | number | boolean | string[] | undefined
  ) => void;
  clearFilters: () => void;
}

export function useStaffFilters({
  initialFilters,
  urlFilters,
  onFiltersChange,
  onResetPagination,
  currentPagination,
}: UseStaffFiltersOptions): UseStaffFiltersReturn {
  const [filters, setFiltersState] =
    useState<Partial<ProfileQueryRequest>>(initialFilters);
  const prevFiltersRef = useRef<Partial<ProfileQueryRequest>>(urlFilters);
  const paginationRef = useRef<PaginationState>(currentPagination);

  useEffect(() => {
    setFiltersState(urlFilters);
  }, [urlFilters]);

  useEffect(() => {
    paginationRef.current = currentPagination;
  }, [currentPagination]);

  useEffect(() => {
    const filtersChanged =
      JSON.stringify(filters) !== JSON.stringify(prevFiltersRef.current);
    const urlFiltersChanged =
      JSON.stringify(urlFilters) !== JSON.stringify(prevFiltersRef.current);

    if (filtersChanged && !urlFiltersChanged) {
      prevFiltersRef.current = filters;
      onFiltersChange(filters, paginationRef.current);
    } else if (urlFiltersChanged) {
      prevFiltersRef.current = urlFilters;
    }
  }, [filters, urlFilters, onFiltersChange]);

  const setFilters = useCallback(
    (newFilters: Partial<ProfileQueryRequest>) => {
      setFiltersState(newFilters);
      onResetPagination();
    },
    [onResetPagination]
  );

  const handleFilterChange = useCallback(
    (
      key: keyof ProfileQueryRequest,
      value: string | number | boolean | string[] | undefined
    ) => {
      setFiltersState((prev) => {
        const newFilters = { ...prev };
        const isEmpty =
          value === undefined ||
          value === "" ||
          value === null ||
          (Array.isArray(value) && value.length === 0);
        if (isEmpty) {
          delete newFilters[key];
        } else {
          newFilters[key] = value as never;
        }
        return newFilters;
      });
      onResetPagination();
    },
    [onResetPagination]
  );

  const clearFilters = useCallback(() => {
    setFiltersState({});
    onResetPagination();
  }, [onResetPagination]);

  return {
    filters,
    setFilters,
    handleFilterChange,
    clearFilters,
  };
}
